import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { colors } from '../styles/GlobalStyles';
import PayPalPayment from './PayPalPayment';
import StripeFrontendPayment from './StripeFrontendPayment';
import { paymentService, PaymentProvider } from '../services/paymentService';
import { backendApiService } from '../services/backendApiService';

const PaymentContainer = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
`;

const PaymentSelector = styled.div`
  background: ${colors.white};
  padding: 15px;
  border-radius: 8px;
  border: 2px solid ${colors.secondary};
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const SelectorTitle = styled.h3`
  color: ${colors.primary};
  margin-bottom: 15px;
  text-align: center;
  font-size: 1.2rem;
  
  @media (min-width: 768px) {
    font-size: 1.4rem;
  }
`;

const PaymentMethods = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  
  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const PaymentMethodButton = styled.button<{ $active: boolean; $available: boolean }>`
  flex: 1;
  padding: 15px 20px;
  border: 2px solid ${props => {
    if (props.$active) return colors.primary;
    if (!props.$available) return '#ccc';
    return colors.secondary;
  }};
  border-radius: 8px;
  background: ${props => {
    if (props.$active) return colors.cream;
    if (!props.$available) return '#f5f5f5';
    return colors.white;
  }};
  color: ${props => {
    if (!props.$available) return '#999';
    return colors.primary;
  }};
  font-size: 1rem;
  font-weight: bold;
  cursor: ${props => props.$available ? 'pointer' : 'not-allowed'};
  transition: all 0.3s ease;
  
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const PaymentMethodIcon = styled.span`
  font-size: 1.2rem;
`;

const PaymentMethodText = styled.span`
  @media (max-width: 767px) {
    font-size: 0.9rem;
  }
`;

const UnavailableText = styled.div`
  color: #999;
  font-size: 0.8rem;
  text-align: center;
  margin-top: 5px;
  font-style: italic;
`;

const BackupInfo = styled.div`
  background: #e8f4fd;
  border: 1px solid #b8daff;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 15px;
  font-size: 0.9rem;
  color: #004085;
  
  strong {
    color: #002752;
  }
`;

interface PaymentChooserProps {
  adults: number;
  children: number;
  bicchieri?: number;
  email: string;
  onPaymentSuccess: (paymentDetails: any) => void;
  onPaymentError: (error: string) => void;
}

const PaymentChooser: React.FC<PaymentChooserProps> = ({
  adults,
  children,
  bicchieri = 0,
  email,
  onPaymentSuccess,
  onPaymentError
}) => {
  const [selectedProvider, setSelectedProvider] = useState<PaymentProvider | null>(null);

  // Ricontrollo "a monte", proprio all'ingresso nella schermata di pagamento: chi ha il form
  // aperto da prima che le iscrizioni venissero chiuse arriva fin qui senza che nessun
  // refresh glielo segnali. Questo è il controllo più vicino al reale addebito che possiamo
  // fare lato client, prima di mostrare i pulsanti di pagamento veri e propri.
  const [statusCheck, setStatusCheck] = useState<'checking' | 'open' | 'closed'>('checking');
  const [closureMessage, setClosureMessage] = useState('');

  useEffect(() => {
    let cancelled = false;
    backendApiService.checkRegistrationStatus().then((status) => {
      if (cancelled) return;
      if (!status.open) {
        setClosureMessage(status.message || 'Le iscrizioni sono state chiuse nel frattempo.');
        setStatusCheck('closed');
      } else {
        setStatusCheck('open');
      }
    });
    return () => { cancelled = true; };
  }, []);

  if (statusCheck === 'checking') {
    return (
      <PaymentContainer>
        <PaymentSelector>
          <SelectorTitle>Verifica disponibilità in corso...</SelectorTitle>
        </PaymentSelector>
      </PaymentContainer>
    );
  }

  if (statusCheck === 'closed') {
    return (
      <PaymentContainer>
        <PaymentSelector>
          <SelectorTitle>⚠️ Iscrizioni chiuse</SelectorTitle>
          <UnavailableText>{closureMessage}</UnavailableText>
          <UnavailableText>Non è possibile procedere con il pagamento.</UnavailableText>
        </PaymentSelector>
      </PaymentContainer>
    );
  }

  // Verifica quali provider sono disponibili
  const availableProviders = paymentService.getAvailableProviders();
  const isPayPalAvailable = paymentService.isProviderAvailable('paypal'); // PayPal abilitato
  const isStripeAvailable = false; // Nascosto all'utente (ma integrazione mantenuta)

  // Se solo PayPal è disponibile, vai direttamente a PayPal
  if (!selectedProvider && isPayPalAvailable && !isStripeAvailable) {
    setSelectedProvider('paypal');
  }

  // Se non è selezionato nessun provider, mostra il selettore
  if (!selectedProvider) {
    return (
      <PaymentContainer>
        <PaymentSelector>
          <SelectorTitle>💳 Scegli il metodo di pagamento</SelectorTitle>
          
          <BackupInfo>
            <strong>💳 Pagamento Sicuro:</strong> Paga con carta di credito tramite Stripe. 
            I tuoi dati sono protetti e crittografati. Il pagamento verrà elaborato immediatamente.
          </BackupInfo>
          
          <PaymentMethods>
            <PaymentMethodButton
              $active={false}
              $available={isPayPalAvailable}
              onClick={() => isPayPalAvailable && setSelectedProvider('paypal')}
              disabled={!isPayPalAvailable}
            >
              <PaymentMethodIcon>💙</PaymentMethodIcon>
              <PaymentMethodText>PayPal</PaymentMethodText>
            </PaymentMethodButton>
            
            <PaymentMethodButton
              $active={false}
              $available={isStripeAvailable}
              onClick={() => isStripeAvailable && setSelectedProvider('stripe')}
              disabled={!isStripeAvailable}
            >
              <PaymentMethodIcon>💳</PaymentMethodIcon>
              <PaymentMethodText>Carta di Credito</PaymentMethodText>
            </PaymentMethodButton>
          </PaymentMethods>
          
          {!isPayPalAvailable && (
            <UnavailableText>
              PayPal non è disponibile in questo momento
            </UnavailableText>
          )}
          
          {!isStripeAvailable && (
            <UnavailableText>
              Stripe non è disponibile in questo momento
            </UnavailableText>
          )}
          
          {availableProviders.length === 0 && (
            <UnavailableText>
              ❌ Nessun metodo di pagamento disponibile. Contatta l'assistenza.
            </UnavailableText>
          )}
        </PaymentSelector>
      </PaymentContainer>
    );
  }

  // Funzione per tornare al selettore
  const handleBack = () => {
    setSelectedProvider(null);
  };

  // Wrapper per gli eventi di successo/errore che includono il tipo di provider
  const handlePaymentSuccess = (paymentDetails: any) => {
    paymentService.logPaymentEvent('Payment Success', {
      provider: selectedProvider,
      amount: paymentDetails.amount,
      paymentType: paymentDetails.paymentType
    });
    
    onPaymentSuccess({
      ...paymentDetails,
      paymentMethod: selectedProvider
    });
  };

  const handlePaymentError = (error: string) => {
    paymentService.logPaymentEvent('Payment Error', {
      provider: selectedProvider,
      error
    });
    
    onPaymentError(error);
  };

  return (
    <PaymentContainer>


      {/* Renderizza il componente del provider selezionato */}
      {selectedProvider === 'paypal' && (
        <PayPalPayment
          adults={adults}
          children={children}
          bicchieri={bicchieri}
          email={email}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentError={handlePaymentError}
        />
      )}

      {selectedProvider === 'stripe' && (
        <StripeFrontendPayment
          adults={adults}
          children={children}
          bicchieri={bicchieri}
          email={email}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentError={handlePaymentError}
        />
      )}
    </PaymentContainer>
  );
};

export default PaymentChooser;
